import { GraphQL } from '@guestgoodie/graphql'
import debounce from 'lodash.debounce'
import { HugeiconsIcon } from '@hugeicons/react'
import { Tick01Icon } from '@hugeicons/core-stroke-rounded'
import { startTransition, useMemo, useState } from 'react'
import { PencilEdit01Icon, Search01Icon } from '@hugeicons/core-solid-rounded'
import * as Popover from '@radix-ui/react-popover'

import {
  Combobox,
  ComboboxItem,
  ComboboxItemCheck,
  ComboboxList,
  ComboboxProvider
} from '@ariakit/react'

import usePaginatedGuests, { QueryVariables } from '../hooks/usePaginatedGuests'
import { useLingui } from '@lingui/react/macro'
import { popoverContainer } from '@/utils/popovers'
import Loading from '@/components/ui/Loading'

function optionFromGuest(guest: GraphQL.GuestFragmentFragment) {
  return {
    value: `${guest.id}`,
    label: `${guest.firstName} ${guest.lastName}${guest.email ? ` (${guest.email})` : ``}`
  }
}

interface Props {
  ids: number[]
  onChange: (ids: number[]) => void
  name?: string
  invalid?: boolean
  disabled?: boolean
  readOnly?: boolean
}

export function SelectGuests({
  ids,
  onChange,
  name,
  invalid,
  readOnly,
  disabled
}: Props) {
  const { t } = useLingui()
  const [open, setOpen] = useState(false)
  const [debouncedSearch, setDebouncedSearch] = useState<string>('')

  const variables = useMemo<QueryVariables>(
    () => ({
      filter: {
        search: debouncedSearch
      },
      sort: {
        direction: GraphQL.SortDirection.Asc,
        field: GraphQL.GuestSortField.LastName
      }
    }),
    [debouncedSearch]
  )

  const updateDebouncedSearch = useMemo(
    () => debounce(setDebouncedSearch, 500),
    [setDebouncedSearch]
  )

  const { items: guests, loading } = usePaginatedGuests(variables)

  const values = useMemo(() => ids.map(id => `${id}`), [ids])
  const items = useMemo(() => (guests ?? []).map(optionFromGuest), [guests])

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          className="size-10 rounded-small bg-primary p-2.5 text-white disabled:cursor-not-allowed"
          tabIndex={0}
          type="button"
          aria-label={t`Edit guest`}
          name={name}
          aria-invalid={invalid}
          disabled={disabled || readOnly}
        >
          <HugeiconsIcon icon={PencilEdit01Icon} className="size-full" />
        </button>
      </Popover.Trigger>
      <Popover.Portal container={popoverContainer}>
        <ComboboxProvider
          open={open}
          setOpen={setOpen}
          selectedValue={values}
          resetValueOnHide
          includesBaseElement={false}
          setSelectedValue={values => {
            onChange(values.map(num => parseInt(num)))
          }}
          setValue={value =>
            startTransition(() => updateDebouncedSearch(value))
          }
        >
          <Popover.Content className="max-h-(--radix-select-content-available-height) max-w-screen data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 overflow-hidden rounded bg-white shadow-lg outline-hidden z-popover">
            <div className="flex flex-row items-center gap-2.5 p-3">
              <HugeiconsIcon icon={Search01Icon} />
              <Combobox
                autoSelect
                placeholder={t`Search Guests`}
                className="w-full rounded-small border border-text bg-white text-regular text-text placeholder:text-text-light outline-hidden py-2 px-4 focus:outline-solid focus:outline-primary"
                // Ariakit's Combobox manually triggers a blur event on virtually
                // blurred items, making them work as if they had actual DOM
                // focus. These blur events might happen after the corresponding
                // focus events in the capture phase, leading Radix Select to
                // close the popover. This happens because Radix Select relies on
                // the order of these captured events to discern if the focus was
                // outside the element. Since we don't have access to the
                // onInteractOutside prop in the Radix SelectContent component to
                // stop this behavior, we can turn off Ariakit's behavior here.
                onBlurCapture={event => {
                  event.preventDefault()
                  event.stopPropagation()
                }}
              />
            </div>
            <ComboboxList className="flex flex-col gap-2 overflow-y-scroll p-3">
              {loading && (
                <div className="flex flex-row w-full p-5 justify-center items-center">
                  <Loading />
                </div>
              )}
              {!loading &&
                items.map(({ label, value }) => (
                  <ComboboxItem
                    key={value}
                    value={value}
                    className="group flex cursor-pointer flex-row items-center gap-2 rounded-small p-2 data-[active-item=true]:bg-primary-bright focus:bg-primary-bright focus:outline-hidden"
                  >
                    <ComboboxItemCheck>
                      <HugeiconsIcon
                        icon={Tick01Icon}
                        className="text-text size-4"
                      />
                    </ComboboxItemCheck>
                    {label}
                  </ComboboxItem>
                ))}
            </ComboboxList>
          </Popover.Content>
        </ComboboxProvider>
      </Popover.Portal>
    </Popover.Root>
  )
}
